<template>
  <div class="loanable-edit-pricing-parameters">
    <div v-for="key in displayedKeys" :key="key">
      <conditional-context
        :class="`loanable-edit-pricing-parameters__${key}`"
        :inset="false"
        show
        :label="capitalize($ct(`parametrized_price.${key}`))"
      >
        <div class="d-flex">
          <span>{{ currency(pricing.parameters[key].minimum) }}</span>
          <span class="flex-grow-1" />
          <span>{{ currency(pricing.parameters[key].maximum) }}</span>
        </div>
        <b-form-input
          :id="`${key}-slider`"
          :name="`${key}-slider`"
          :value="loanablePricing[key] ?? pricing.parameters[key].current"
          type="range"
          :min="pricing.parameters[key].minimum"
          :max="pricing.parameters[key].maximum"
          :step="0.01"
          :disabled="isCustomPricingParameterDisabled(key)"
          :aria-valuenow="loanablePricing[key]"
          :aria-valuetext="`${loanablePricing[key]} $`"
          :aria-label="$ct(`parametrized_price.${key}`)"
          :aria-valuemin="pricing.parameters[key].minimum"
          :aria-valuemax="pricing.parameters[key].maximum"
          role="slider"
          @input="handleSlider(key, $event)"
        />
        <simple-price
          class="mb-3"
          :free-label="currency(0)"
          :label="simplePriceLabel(key)"
          :value="loanablePricing[key] ?? pricing.parameters[key].current"
        />

        <p>
          <b-link
            :disabled="loanablePricing[key] === null"
            @click.prevent.stop="
              !isCustomPricingParameterDisabled(key) &&
                setLoanableParameters(key, $event ? null : pricing.parameters[key].current)
            "
          >
            {{
              capitalize(
                $t("components.loanable.loanable_edit_pricing_parameters.form.use_default")
              )
            }}
          </b-link>
        </p>
      </conditional-context>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n-composable";

import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import SimplePrice from "@/components/Loan/SimplePrice.vue";

import { capitalize, currency } from "@/helpers/filters";

import type {
  Loanable,
  Persisted,
  PricingParameter,
  Pricing,
  PricingParameterComponent,
} from "@/types/models";

const { t } = useI18n();

const props = defineProps<{
  pricing: Persisted<Pricing>;
  value: Loanable["parameters"];
  customPricingDisabled: boolean;
}>();

const emit = defineEmits<{
  (e: "update", payload: Loanable["parameters"]): void;
}>();

const customPricingParameterKeys: Array<keyof PricingParameter<PricingParameterComponent>> = [
  "distance",
  "hourly",
];

const loanablePricing = computed<PricingParameter<number | null>>(() => {
  const index = props.pricing.community_id ?? 0;
  if (props.value && props.value[index]) {
    return props.value[index];
  }

  return {
    distance: null,
    hourly: null,
    minimum: null,
  };
});

const displayedKeys = computed(() => {
  return (["distance", "hourly", "minimum"] as Array<keyof PricingParameter>).filter((key) => {
    const { minimum, maximum } = props.pricing.parameters[key];
    return minimum !== maximum && maximum !== 0;
  });
});

function $ct(key: string) {
  return t(`components.loanable.loanable_edit_pricing_parameters.${key}`);
}

function handleSlider(key: keyof PricingParameter, value: string) {
  const newLoanablePricing = {
    ...loanablePricing.value,
  };
  newLoanablePricing[key] = Number.parseFloat(value);
  emit("update", newLoanablePricing);
}

function setLoanableParameters(key: keyof PricingParameter, value: number | null) {
  const newLoanablePricing = {
    ...loanablePricing.value,
  };
  newLoanablePricing[key] = value;
  emit("update", newLoanablePricing);
}

function simplePriceLabel(key: keyof PricingParameter): string {
  if (loanablePricing.value[key] === null) {
    return `${capitalize($ct(`parametrized_price.short_${key}`))} (${$ct(
      `parametrized_price.default`
    )})`;
  }
  return capitalize($ct(`parametrized_price.short_${key}`));
}

watch(
  () => props.customPricingDisabled,
  (disabled) => {
    if (!disabled) {
      return;
    }

    customPricingParameterKeys.forEach((key) => {
      setLoanableParameters(key, null);
    });
  }
);

function isCustomPricingParameterDisabled(
  key: keyof PricingParameter<PricingParameterComponent>
): boolean {
  return props.customPricingDisabled && customPricingParameterKeys.includes(key);
}
</script>

<style lang="scss">
.loanable-edit-pricing-parameters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  > div {
    flex: 1 1 14rem;
  }
}
</style>
